import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { marketingApi } from "@/lib/marketing";
import { Field, TextInput, Select, PURPOSES } from "./shared";

const DAY_OPTIONS = [7, 14, 30, 90];

const FUNNEL_STEPS = [
  { key: "clicks", label: "Clicks" },
  { key: "visitors", label: "Visitors (landed)" },
  { key: "audit_started", label: "Audit Started" },
  { key: "audit_completed", label: "Audit Completed" },
  { key: "bookings", label: "Consultation Booked" },
];

function FunnelBar({ label, value, max, testId }) {
  const pct = max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <span className="font-body text-xs uppercase tracking-[0.1em] text-ink-soft w-40 shrink-0">{label}</span>
      <div className="flex-1 h-6 bg-paper-deep relative overflow-hidden">
        <div className="h-full bg-terracotta transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="font-serif text-lg text-ink w-12 text-right">{value}</span>
    </div>
  );
}

const CONVERSION_RATE_LABELS = [
  { key: "click_to_visitor", label: "Click → Visitor" },
  { key: "visitor_to_audit_started", label: "Visitor → Audit Start" },
  { key: "audit_started_to_completed", label: "Audit Start → Complete" },
  { key: "audit_completed_to_booking", label: "Audit Complete → Booking" },
  { key: "click_to_booking", label: "Click → Booking (overall)" },
];

function formatPct(value) {
  return value == null ? "—" : `${Math.round(value * 1000) / 10}%`;
}

function ConversionRates({ rates }) {
  if (!rates) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="performance-conversion-rates">
      {CONVERSION_RATE_LABELS.map(({ key, label }) => (
        <div key={key} className="bg-paper border border-ink/10 p-3 text-center" data-testid={`conversion-rate-${key}`}>
          <p className="font-serif text-xl text-ink">{formatPct(rates[key])}</p>
          <p className="font-body text-[10px] uppercase tracking-[0.08em] text-ink-soft mt-1">{label}</p>
        </div>
      ))}
    </div>
  );
}

function BreakdownTable({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div>
      <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-3">Compare by link</p>
      <div className="bg-paper border border-ink/10 overflow-x-auto" data-testid="performance-breakdown-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-ink/10 text-left font-body text-[10px] uppercase tracking-[0.1em] text-ink-soft">
              <th className="p-3">Link</th>
              <th className="p-3">Channel</th>
              <th className="p-3">Campaign</th>
              <th className="p-3 text-right">Clicks</th>
              <th className="p-3 text-right">Visitors</th>
              <th className="p-3 text-right">Audit Start</th>
              <th className="p-3 text-right">Audit Complete</th>
              <th className="p-3 text-right">Bookings</th>
              <th className="p-3 text-right">Click→Booking</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.link_id} className="border-b border-ink/5 font-body" data-testid={`breakdown-row-${row.link_id}`}>
                <td className="p-3 text-ink">{row.name}</td>
                <td className="p-3 text-ink-soft">{row.channel || "—"}</td>
                <td className="p-3 text-ink-soft">{row.campaign_key || "—"}</td>
                <td className="p-3 text-right text-ink">{row.funnel.clicks}</td>
                <td className="p-3 text-right text-ink">{row.funnel.visitors}</td>
                <td className="p-3 text-right text-ink">{row.funnel.audit_started}</td>
                <td className="p-3 text-right text-ink">{row.funnel.audit_completed}</td>
                <td className="p-3 text-right text-ink">{row.funnel.bookings}</td>
                <td className="p-3 text-right text-ink-soft">{formatPct(row.conversion_rates.click_to_booking)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PerformanceResults({ data, loading, error }) {
  if (loading) return <p className="font-body text-sm text-ink-soft py-8" data-testid="performance-loading">Loading performance…</p>;
  if (error) return <p className="font-body text-sm text-terracotta py-8" data-testid="performance-error">{error}</p>;
  if (!data) return null;

  const maxFunnel = Math.max(1, ...FUNNEL_STEPS.map((s) => data.funnel[s.key] || 0));

  return (
    <div className="space-y-8" data-testid="performance-results">
      {typeof data.link_count === "number" && (
        <p className="font-body text-xs uppercase tracking-[0.14em] text-ink-soft" data-testid="performance-link-count">
          {data.link_count} matching link{data.link_count === 1 ? "" : "s"}
        </p>
      )}

      <div>
        <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-3">Clicks &amp; visitors over time</p>
        <div className="h-56 min-h-[224px] bg-paper border border-ink/10 p-2" data-testid="performance-timeseries-chart">
          {data.time_series.length === 0 ? (
            <div className="h-full flex items-center justify-center font-body text-sm text-ink-soft">No activity in this window yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={208}>
              <AreaChart data={data.time_series} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="clicksGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#90533B" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#90533B" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="visitorsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8F9562" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#8F9562" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#49523522" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#6B7452" }} />
                <YAxis tick={{ fontSize: 11, fill: "#6B7452" }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#F2EBDA", border: "1px solid #49523522", fontSize: 12 }} />
                <Area type="monotone" dataKey="clicks" name="Clicks" stroke="#90533B" fill="url(#clicksGradient)" strokeWidth={2} />
                <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#8F9562" fill="url(#visitorsGradient)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div>
        <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-3">Funnel</p>
        <div className="space-y-2.5 bg-paper border border-ink/10 p-5" data-testid="performance-funnel">
          {FUNNEL_STEPS.map((step) => (
            <FunnelBar key={step.key} label={step.label} value={data.funnel[step.key] || 0} max={maxFunnel} testId={`funnel-step-${step.key}`} />
          ))}
        </div>
      </div>

      <div>
        <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-3">Conversion rates</p>
        <ConversionRates rates={data.conversion_rates} />
      </div>

      {data.breakdown && <BreakdownTable rows={data.breakdown} />}

      <div>
        <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-3">Attributed Commercial Value</p>
        <div className="bg-paper-deep border border-ink/10 p-5" data-testid="performance-commercial">
          <p className="font-body text-sm text-ink-soft">
            {data.commercial.attributed_commercial_value == null ? "Not yet available per link/filter — see PostHog directly for deeper commercial analysis." : data.commercial.attributed_commercial_value}
          </p>
          <p className="font-body text-xs text-ink-soft/70 mt-2">{data.commercial.note}</p>
        </div>
      </div>
    </div>
  );
}

export function LinkPerformance({ linkId }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    marketingApi.getLinkPerformance(linkId, days)
      .then((d) => { if (active) setData(d); })
      .catch((err) => { if (active) setError(err.message || "Failed to load performance"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [linkId, days]);

  return (
    <div data-testid="link-performance-panel">
      <div className="flex items-center gap-3 mb-4">
        <span className="font-body text-xs uppercase tracking-[0.14em] text-ink-soft">Window</span>
        <Select value={String(days)} onChange={(v) => setDays(Number(v))} options={DAY_OPTIONS.map(String)} data-testid="link-performance-days" />
      </div>
      <PerformanceResults data={data} loading={loading} error={error} />
    </div>
  );
}

export default function PerformancePanel() {
  const [days, setDays] = useState(30);
  const [channel, setChannel] = useState("");
  const [placement, setPlacement] = useState("");
  const [campaignKey, setCampaignKey] = useState("");
  const [purpose, setPurpose] = useState("");
  const [offerKey, setOfferKey] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    marketingApi.getAnalyticsOverview({ days, channel, placement, campaign_key: campaignKey, purpose, offer_key: offerKey })
      .then(setData)
      .catch((err) => setError(err.message || "Failed to load analytics"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-8" data-testid="performance-panel">
      <div className="bg-paper border border-ink/12 p-6">
        <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-4">Filters</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4">
          <Field label="Window (days)"><Select value={String(days)} onChange={(v) => setDays(Number(v))} options={DAY_OPTIONS.map(String)} data-testid="overview-filter-days" /></Field>
          <Field label="Channel"><TextInput value={channel} onChange={(e) => setChannel(e.target.value)} placeholder="e.g. linkedin" data-testid="overview-filter-channel" /></Field>
          <Field label="Placement"><TextInput value={placement} onChange={(e) => setPlacement(e.target.value)} placeholder="e.g. post" data-testid="overview-filter-placement" /></Field>
          <Field label="Campaign key"><TextInput value={campaignKey} onChange={(e) => setCampaignKey(e.target.value)} data-testid="overview-filter-campaign" /></Field>
          <Field label="Purpose"><Select value={purpose} onChange={setPurpose} options={PURPOSES} includeEmpty data-testid="overview-filter-purpose" /></Field>
          <Field label="Offer key"><TextInput value={offerKey} onChange={(e) => setOfferKey(e.target.value)} data-testid="overview-filter-offer" /></Field>
        </div>
        <button type="button" onClick={load} data-testid="overview-apply-filters"
          className="mt-5 bg-ink text-paper px-5 py-2.5 font-body text-xs uppercase tracking-[0.14em] hover:bg-terracotta transition-colors">
          Apply filters
        </button>
      </div>
      <PerformanceResults data={data} loading={loading} error={error} />
    </div>
  );
}
